import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import PoolAdminRoute from '../components/PoolAdminRoute'
import LoadingSpinner from '../components/LoadingSpinner'
import { 
  DocumentArrowUpIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  HomeIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface RaceData {
  track: string
  date: string
  total_races: number
  races: Array<{
    race_number: number
    post_time: string
    race_type: string
    conditions: string
    purse: string
    distance?: string
    entries: Array<{
      post_position: string
      post_position_color?: {
        background_color: string
        text_color: string
        name: string
      }
      horse_name: string
      horse_id: string
      age_sex: string
      jockey: string
      trainer: string
      weight: string
      medication?: string
    }>
  }>
}

interface PoolData {
  name: string
  description: string
  maxParticipants: number
  entryFee: number
  pickDeadline: string
}

export default function CreateHorseRacingPoolPage() {
  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  
  const [poolData, setPoolData] = useState<PoolData>({
    name: '',
    description: '',
    maxParticipants: 50,
    entryFee: 25,
    pickDeadline: ''
  })
  
  const [raceData, setRaceData] = useState<RaceData | null>(null)
  const [jsonFile, setJsonFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [processingFile, setProcessingFile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'upload' | 'configure' | 'review'>('upload')

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    await processFile(file)
  }

  const processFile = async (file: File) => {
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file')
      return
    }

    try {
      setProcessingFile(true)
      setError(null)
      setJsonFile(file)
      
      console.log('Processing file:', file.name, 'Size:', file.size)
      
      const text = await file.text()
      console.log('File text length:', text.length)
      
      const data = JSON.parse(text) as RaceData
      console.log('Parsed data:', { track: data.track, raceCount: data.races?.length })
      
      // Validate the JSON structure
      if (!data.track || !data.races || !Array.isArray(data.races)) {
        throw new Error('Invalid race data format. Expected track and races array.')
      }

      setRaceData(data)
      
      // Auto-populate some pool data from the race data
      const raceDate = data.date || new Date().toISOString().split('T')[0]
      
      // Calculate pick deadline safely
      let pickDeadline = ''
      if (data.races[0]?.post_time && raceDate) {
        try {
          // Parse the post time (e.g., "1:00 PM ET")
          const postTime = data.races[0].post_time
          // For now, just set to race date at 12:00 PM as default
          const deadlineDate = new Date(`${raceDate}T12:00:00`)
          if (!isNaN(deadlineDate.getTime())) {
            pickDeadline = deadlineDate.toISOString().slice(0, 16)
          }
        } catch (err) {
          console.warn('Could not parse post time:', data.races[0].post_time)
        }
      }
      
      setPoolData(prev => ({
        ...prev,
        name: `${data.track} - ${raceDate}`,
        description: `Horse racing pool for ${data.track} on ${raceDate} with ${data.total_races || data.races.length} races`,
        pickDeadline
      }))
      
      setStep('configure')
      toast.success(`Loaded ${data.races.length} races from ${data.track}`)
    } catch (err) {
      console.error('Error parsing JSON:', err)
      setError(err instanceof Error ? err.message : 'Failed to parse JSON file')
    } finally {
      setProcessingFile(false)
    }
  }

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      await processFile(files[0])
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handlePoolDataChange = (field: keyof PoolData, value: string | number) => {
    setPoolData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const createPool = async () => {
    if (!raceData) {
      setError('No race data loaded')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const token = await getAccessTokenSilently()
      
      const poolPayload = {
        name: poolData.name,
        description: poolData.description,
        type: 'horse-racing',
        startDate: raceData.date,
        endDate: raceData.date,
        maxParticipants: poolData.maxParticipants,
        entryFee: poolData.entryFee,
        pickDeadline: poolData.pickDeadline,
        settings: {
          track: raceData.track,
          raceDate: raceData.date,
          totalRaces: raceData.total_races || raceData.races.length,
          scoringType: 'win_place_show'
        },
        raceData: raceData
      }

      const response = await fetch(`${API_BASE_URL}/pools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(poolPayload)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Failed to create pool: ${response.statusText}`)
      }

      const result = await response.json()
      toast.success('Horse racing pool created successfully!')
      navigate(`/admin/pools/horse-racing/${result.poolId}`)
      
    } catch (err) {
      console.error('Error creating pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to create pool')
      toast.error('Failed to create pool')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setRaceData(null)
    setJsonFile(null)
    setPoolData({
      name: '',
      description: '',
      maxParticipants: 50,
      entryFee: 25,
      pickDeadline: ''
    })
    setStep('upload')
    setError(null)
  }

  return (
    <PoolAdminRoute>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to="/admin/pools/horse-racing"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  Horse Racing Pools
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  Create Pool
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
            Create Horse Racing Pool
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload a race card JSON file and configure your horse racing pool
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              <li className="relative pr-8 sm:pr-20">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                  step === 'upload' ? 'bg-yellow-600' : 'bg-yellow-600'
                } text-white`}>
                  {step !== 'upload' ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">1</span>
                  )}
                </div>
                <span className="absolute top-10 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Upload JSON
                </span>
              </li>
              
              <li className="relative pr-8 sm:pr-20">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                  step === 'configure' ? 'bg-yellow-600' : 
                  step === 'review' ? 'bg-yellow-600' : 'bg-gray-200 dark:bg-gray-700'
                } text-white`}>
                  {step === 'review' ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">2</span>
                  )}
                </div>
                <span className="absolute top-10 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Configure
                </span>
              </li>
              
              <li className="relative">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  step === 'review' ? 'bg-yellow-600' : 'bg-gray-200 dark:bg-gray-700'
                } text-white`}>
                  <span className="text-sm font-medium">3</span>
                </div>
                <span className="absolute top-10 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Review
                </span>
              </li>
            </ol>
          </nav>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Upload JSON */}
        {step === 'upload' && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Upload Race Card JSON
              </h3>
              
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      JSON Format Expected
                    </h3>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <p>Upload a JSON file containing race data with the following structure:</p>
                      <ul className="mt-2 list-disc list-inside">
                        <li>track: Track name (e.g., "Keeneland")</li>
                        <li>date: Race date</li>
                        <li>races: Array of race objects with entries</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div 
                className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md hover:border-yellow-400 transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-1 text-center">
                  {processingFile ? (
                    <>
                      <LoadingSpinner size="lg" className="mx-auto text-yellow-600" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Processing JSON file...
                      </p>
                    </>
                  ) : (
                    <>
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600 dark:text-gray-400">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-yellow-600 hover:text-yellow-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-yellow-500"
                        >
                          <span>Upload a JSON file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            accept=".json"
                            className="sr-only"
                            onChange={handleFileUpload}
                            disabled={processingFile}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        JSON files only
                      </p>
                    </>
                  )}
                </div>
              </div>

              {jsonFile && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                    <span className="text-sm text-green-800 dark:text-green-200">
                      File loaded: {jsonFile.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Configure Pool */}
        {step === 'configure' && raceData && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Configure Pool Settings
              </h3>

              {/* Race Data Summary */}
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  Race Card Summary
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-yellow-700 dark:text-yellow-300">Track:</span>
                    <div className="font-medium">{raceData.track}</div>
                  </div>
                  <div>
                    <span className="text-yellow-700 dark:text-yellow-300">Date:</span>
                    <div className="font-medium">{raceData.date}</div>
                  </div>
                  <div>
                    <span className="text-yellow-700 dark:text-yellow-300">Races:</span>
                    <div className="font-medium">{raceData.races.length}</div>
                  </div>
                  <div>
                    <span className="text-yellow-700 dark:text-yellow-300">Total Entries:</span>
                    <div className="font-medium">
                      {raceData.races.reduce((sum, race) => sum + (race.entries?.length || 0), 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pool Configuration Form */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="pool-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pool Name
                  </label>
                  <input
                    type="text"
                    id="pool-name"
                    value={poolData.name}
                    onChange={(e) => handlePoolDataChange('name', e.target.value)}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="pool-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    id="pool-description"
                    rows={3}
                    value={poolData.description}
                    onChange={(e) => handlePoolDataChange('description', e.target.value)}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="max-participants" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Max Participants
                    </label>
                    <input
                      type="number"
                      id="max-participants"
                      min="1"
                      max="500"
                      value={poolData.maxParticipants}
                      onChange={(e) => handlePoolDataChange('maxParticipants', parseInt(e.target.value))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="entry-fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Entry Fee ($)
                    </label>
                    <input
                      type="number"
                      id="entry-fee"
                      min="0"
                      step="0.01"
                      value={poolData.entryFee}
                      onChange={(e) => handlePoolDataChange('entryFee', parseFloat(e.target.value))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="pick-deadline" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pick Deadline
                  </label>
                  <input
                    type="datetime-local"
                    id="pick-deadline"
                    value={poolData.pickDeadline}
                    onChange={(e) => handlePoolDataChange('pickDeadline', e.target.value)}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Participants must submit picks before this time
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep('review')}
                  disabled={!poolData.name || !poolData.pickDeadline}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && raceData && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Review & Create Pool
              </h3>

              <div className="space-y-6">
                {/* Pool Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Pool Details</h4>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">{poolData.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Track</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">{raceData.track}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Race Date</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">{raceData.date}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Number of Races</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">{raceData.races.length}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Max Participants</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">{poolData.maxParticipants}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Entry Fee</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">${poolData.entryFee}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Pick Deadline</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">
                        {new Date(poolData.pickDeadline).toLocaleString()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Scoring</dt>
                      <dd className="text-sm text-gray-900 dark:text-white">Win (6pts), Place (3pts), Show (1pt) - Cumulative</dd>
                    </div>
                  </dl>
                </div>

                {/* Race Preview */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Race Preview</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 max-h-64 overflow-y-auto">
                    {raceData.races.slice(0, 5).map((race, index) => (
                      <div key={index} className="mb-3 last:mb-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Race {race.race_number} - {race.post_time}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {race.race_type} • {race.entries?.length || 0} entries • ${race.purse}
                        </div>
                      </div>
                    ))}
                    {raceData.races.length > 5 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        ... and {raceData.races.length - 5} more races
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep('configure')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Back
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    Start Over
                  </button>
                  <button
                    type="button"
                    onClick={createPool}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Creating Pool...
                      </>
                    ) : (
                      'Create Pool'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PoolAdminRoute>
  )
}
